export const customerDashboardPath = '/account'
export const insuranceOnboardingPath = '/insurance'
export const storePrefixPath = '/store'
export const storeHomePath = '/'
export const signupPath = '/get-started'
export const loginPath = '/login'
export const rootPath = '/'
export const detailPagePrefixPath = '/s'
export const detailPagePattern = /^\/s\/[^/]+\/\d+\/?$/
export const dpDetailPagePattern = /^\/dp\/[^/]+\/\d+\/?$/
/** Exact provider directory detail (`/provider/{slug}`), not request-appointment. */
export const providerDetailPagePattern = /^\/provider\/[^/]+\/?$/
/** Exact pharmacy directory detail (`/pharmacy/{name}/{id}`), not request-prescription. */
export const pharmacyDetailPagePattern = /^\/pharmacy\/[^/]+\/[^/]+\/?$/
export const generalAiPath = '/ai'
export const myCarePath = '/account/my-care'
export const dashboardHomePath = '/account'
export const nextStepsPath = '/account/next-steps'

/** `/account` sub-routes that mount `AccountDashboardShell`. */
export const accountDashboardShellRoutePrefixes: readonly string[] = [
    '/account/my-care',
    '/account/medications',
    '/account/visits',
    '/account/updates',
    '/account/orders',
    '/account/labs',
    '/account/imaging',
    '/account/care-plans',
    nextStepsPath,
    '/account/messages',
]

/**
 * Account sections linked off the dashboard home that don't carry their own
 * bottom-nav / side-nav tab (see `DEFAULT_BOTTOM_NAV_ITEMS`), so the Home tab
 * stays highlighted while browsing them.
 */
export const dashboardHomeOnlySubsections: readonly string[] = [
    `${dashboardHomePath}/orders`,
    `${dashboardHomePath}/labs`,
    `${dashboardHomePath}/imaging`,
    `${dashboardHomePath}/visits`,
    nextStepsPath,
]

export function isAccountDashboardShellRoutePrefix(path: string): boolean {
    return accountDashboardShellRoutePrefixes.some(
        (prefix) => path === prefix || path.startsWith(`${prefix}/`),
    )
}

// Public routes that don't require authentication
export const publicRoutes: readonly string[] = [
    '/faq',
    '/terms-of-service',
    '/privacy-policy',
    '/hipaa-privacy-practices',
    '/telehealth-informed-consent',
    '/assignment-of-benefits',
    '/vital-informed-consent',
    '/clinician-hiring',
    '/work-with-us',
    '/all-care',
    '/care-options',
]

// Auth-walled pages whose SSR is a static, PHI-free metadata shell. Link-preview
// crawlers may fetch these so shared links resolve their own Open Graph tags
// rather than the homepage fallback.
export const shareableMetadataRoutes: readonly string[] = ['/ai/chat']

export function isShareableMetadataPage(path: string): boolean {
    return shareableMetadataRoutes.includes(path)
}

export function getStoreCategoryPath(category: string): string {
    return `${storePrefixPath}/${category}`
}

export function isCustomerDashboardPage(path: string): boolean {
    return [customerDashboardPath].includes(path)
}

export function isAnyCustomerDashboardPage(path: string): boolean {
    return isCustomerDashboardPage(path) || path.startsWith(`${customerDashboardPath}/`)
}

export function isStoreHomePage(path: string): boolean {
    return [storePrefixPath, storeHomePath, rootPath].includes(path)
}

export function isAnyStorePage(path: string): boolean {
    return isStoreHomePage(path) || path.startsWith(`${storePrefixPath}/`)
}

export function isAllServicesStorePath(path: string): boolean {
    return /^\/store\/[^/]+\/all-services/.test(path)
}

export function isAnyDetailPage(path: string): boolean {
    return path === detailPagePrefixPath || path.startsWith(`${detailPagePrefixPath}/`)
}

export function isDetailPage(path: string): boolean {
    return isAnyDetailPage(path) && detailPagePattern.test(path)
}

export function isDpDetailPage(path: string): boolean {
    return dpDetailPagePattern.test(path)
}

/** Exact SKU product detail URLs (`/s/.../...` or `/dp/.../...`), not booking subpaths. */
export function isSkuProductDetailPage(path: string): boolean {
    return isDetailPage(path) || isDpDetailPage(path)
}

/** Exact provider directory detail URL (`/provider/{slug}`), not request-appointment. */
export function isProviderDetailPage(path: string): boolean {
    return providerDetailPagePattern.test(path)
}

/** Exact pharmacy directory detail URL (`/pharmacy/{name}/{id}`), not request-prescription. */
export function isPharmacyDetailPage(path: string): boolean {
    return pharmacyDetailPagePattern.test(path)
}

export function isAnyInsurancePage(path: string): boolean {
    return path === insuranceOnboardingPath || path.startsWith(`${insuranceOnboardingPath}/`)
}

export function isAnySignupPath(path: string): boolean {
    return path.startsWith(signupPath) || path.startsWith(loginPath)
}

export function isFocusedFlow(path: string): boolean {
    const isVisitFlow = path.startsWith('/visit/')
    const isOrdersFlow = path.startsWith('/orders/')
    const isInPersonVisitFlow = path.startsWith('/in-person-visit/')
    const isUrgentCareFlow = path.startsWith('/urgent-care/')
    const isVerificationFlow = path.startsWith('/verification/')
    const isDetailDeepFlow = /^\/s\/[^/]+\/[^/]+\/.+/.test(path)
    return (
        isVisitFlow ||
        isOrdersFlow ||
        isInPersonVisitFlow ||
        isUrgentCareFlow ||
        isDetailDeepFlow ||
        isVerificationFlow
    )
}

export function isAnyDashboardHomePage(path: string): boolean {
    if (isAccountDashboardShellRoutePrefix(path)) {
        return true
    }

    if (path === dashboardHomePath) {
        return typeof document !== 'undefined' && document.documentElement.dataset.accountDashboard === 'true'
    }

    return false
}

export function isGeneralAiLander(path: string): boolean {
    return path === generalAiPath
}

export function isRoutedPage(path: string): boolean {
    return publicRoutes.includes(path)
}

export function isHealthCheckPath(path: string): boolean {
    return path.match(/^\/status\/?$/) !== null
}